import React, { useState, useEffect, useCallback } from "react";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import CoachingTabs from "./CoachingTabs";
import CoachingSkeleton from "../../components/layout/CoachingSkeleton";
import {
    UserPlus, Search, Clock, CheckCircle2, XCircle, Send
} from "lucide-react";

export default function FindMentor() {
    const { isDarkMode } = useTheme();
    const [mentor, setMentor] = useState(null);
    const [mentors, setMentors] = useState([]);
    const [requests, setRequests] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [tab, setTab] = useState("browse");

    const avatarColors = [
        "from-primary-500 to-teal-600",
        "from-blue-500 to-indigo-600",
        "from-violet-500 to-purple-600",
        "from-rose-500 to-pink-600",
        "from-amber-500 to-orange-600",
        "from-cyan-500 to-sky-600",
    ];
    const getAvatarColor = (id) => avatarColors[(id || 0) % avatarColors.length];

    const fetchMentor = useCallback(async () => {
        try { const res = await api.get("/coach/my-mentor"); setMentor(res.data.mentor || null); } catch { setMentor(null); }
    }, []);
    const fetchMentors = useCallback(async () => {
        try { const res = await api.get("/coach/list"); setMentors(res.data.mentors || []); } catch { setMentors([]); }
    }, []);
    const fetchRequests = useCallback(async () => {
        try { const res = await api.get("/coach/my-requests"); setRequests(res.data.requests || []); } catch { setRequests([]); }
    }, []);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([fetchMentor(), fetchMentors(), fetchRequests()]);
            setLoading(false);
        };
        load();
    }, [fetchMentor, fetchMentors, fetchRequests]);

    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 4000);
    };

    const sendRequest = async (coachAdminId) => {
        setActionLoading(true);
        try {
            await api.post("/coach/request", { coach_admin_id: coachAdminId });
            showMessage("Request sent successfully!");
            await fetchRequests();
        } catch (err) {
            showMessage(err.response?.data?.error || "Failed to send request", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const hasPendingTo = (adminId) =>
        requests.some((r) => r.CoachAdminID === adminId && r.Status === "pending");

    const pendingCount = requests.filter((r) => r.Status === "pending").length;
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const filteredMentors = mentors.filter((mentorItem) => {
        if (!normalizedSearchTerm) {
            return true;
        }

        const fullName = `${mentorItem.FirstName || ""} ${mentorItem.LastName || ""}`.trim().toLowerCase();
        const email = String(mentorItem.Email || "").toLowerCase();
        return fullName.includes(normalizedSearchTerm) || email.includes(normalizedSearchTerm);
    });

    if (loading) {
        return <CoachingSkeleton isDarkMode={isDarkMode} />;
    }

    const tabs = [
        { key: "browse", label: "Browse Coaches", icon: Search, badge: mentors.length },
        { key: "requests", label: "My Requests", icon: Send, badge: pendingCount },
    ];

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-6 md:py-8">
                <header className="mb-6">
                    <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                        Find a Partner Coach
                    </h1>
                    <p className={isDarkMode ? "text-slate-400" : "text-slate-600"}>
                        Browse available coaches, send requests, and track their status
                    </p>
                </header>

                <CoachingTabs className="md:hidden mb-6" />

                {message && (
                    <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium transition-all ${
                        message.type === "error"
                            ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>
                        {message.type === "error" ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
                        {message.text}
                    </div>
                )}

                {/* Tabs */}
                <div className={`flex gap-1 p-1 rounded-xl mb-7 ${isDarkMode ? "bg-slate-800/70" : "bg-slate-100"}`} role="tablist">
                    {tabs.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                role="tab"
                                aria-selected={active}
                                onClick={() => setTab(t.key)}
                                className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-3 py-2.5 rounded-lg text-xs md:text-sm font-semibold whitespace-nowrap transition-all ${
                                    active
                                        ? isDarkMode ? "bg-slate-700 text-white shadow-sm" : "bg-white text-slate-900 shadow-sm"
                                        : isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700"
                                }`}
                            >
                                <Icon size={16} />
                                <span>{t.label}</span>
                                {t.badge > 0 && (
                                    <span className={`text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full ${
                                        active ? "bg-primary-500 text-white" : isDarkMode ? "bg-slate-600 text-slate-300" : "bg-slate-200 text-slate-600"
                                    }`}>
                                        {t.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Browse Coaches Tab */}
                {tab === "browse" && (
                    <>
                        <div className={`rounded-2xl border p-4 mb-5 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                            <div className="relative">
                                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} size={18} />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder="Search coaches by name or email"
                                    className={`w-full rounded-xl border pl-10 pr-4 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDarkMode ? "bg-slate-900 border-slate-600 text-slate-100 placeholder:text-slate-500" : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"}`}
                                />
                            </div>
                            <p className={`mt-3 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                {filteredMentors.length} of {mentors.length} coaches shown
                            </p>
                        </div>

                        {mentors.length === 0 ? (
                            <div className={`rounded-2xl border p-12 text-center ${isDarkMode ? "bg-slate-800/60 border-slate-700/60" : "bg-white border-slate-200"}`}>
                                <div className={`w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center ${isDarkMode ? "bg-slate-700/60" : "bg-slate-100"}`}>
                                    <Search className={isDarkMode ? "text-slate-500" : "text-slate-300"} size={36} />
                                </div>
                                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                                    No coaches available
                                </h3>
                                <p className={`text-sm max-w-sm mx-auto ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    Check back soon — new coaches are being added regularly.
                                </p>
                            </div>
                        ) : filteredMentors.length === 0 ? (
                            <div className={`rounded-2xl border p-12 text-center ${isDarkMode ? "bg-slate-800/60 border-slate-700/60" : "bg-white border-slate-200"}`}>
                                <div className={`w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center ${isDarkMode ? "bg-slate-700/60" : "bg-slate-100"}`}>
                                    <Search className={isDarkMode ? "text-slate-500" : "text-slate-300"} size={36} />
                                </div>
                                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                                    No matching coaches
                                </h3>
                                <p className={`text-sm max-w-sm mx-auto ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    Try a different name or email address.
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-4 sm:grid-cols-2">
                                {filteredMentors.map((m) => {
                                    const isPending = hasPendingTo(m.AdminID);
                                    const isCurrentMentor = mentor?.AdminID === m.AdminID;
                                    return (
                                        <div
                                            key={m.PartnerID}
                                            className={`group rounded-2xl border p-5 transition-all hover:shadow-md ${
                                                isCurrentMentor
                                                    ? isDarkMode ? "bg-primary-900/20 border-primary-800/50 ring-1 ring-primary-700/30" : "bg-primary-50/50 border-primary-200 ring-1 ring-primary-200"
                                                    : isDarkMode ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "bg-white border-slate-200 hover:border-slate-300"
                                            }`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getAvatarColor(m.PartnerID)} flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0`}>
                                                    {m.FirstName?.[0]}{m.LastName?.[0]}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className={`font-bold text-[15px] ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                                        {m.FirstName} {m.LastName}
                                                    </h3>
                                                    <p className={`text-xs mt-0.5 truncate ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                                        {m.Email}
                                                    </p>
                                                    {m.JoinedDate && (
                                                        <p className={`text-xs mt-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                                            Coach since {m.JoinedDate}
                                                        </p>
                                                    )}
                                                    <div className="mt-3">
                                                        {isCurrentMentor ? (
                                                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 bg-primary-100 px-3 py-1.5 rounded-lg">
                                                                <CheckCircle2 size={13} /> Your Coach
                                                            </span>
                                                        ) : isPending ? (
                                                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg ${
                                                                isDarkMode ? "text-amber-300 bg-amber-900/30" : "text-amber-700 bg-amber-50"
                                                            }`}>
                                                                <Clock size={13} /> Request Pending
                                                            </span>
                                                        ) : (
                                                            <button
                                                                onClick={() => sendRequest(m.AdminID)}
                                                                disabled={actionLoading}
                                                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 shadow-sm shadow-primary-600/20"
                                                            >
                                                                <UserPlus size={13} /> Send Request
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* Requests Tab */}
                {tab === "requests" && (
                    <>
                        {requests.length === 0 ? (
                            <div className={`rounded-2xl border p-12 text-center ${isDarkMode ? "bg-slate-800/60 border-slate-700/60" : "bg-white border-slate-200"}`}>
                                <div className={`w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center ${isDarkMode ? "bg-slate-700/60" : "bg-slate-100"}`}>
                                    <Send className={isDarkMode ? "text-slate-500" : "text-slate-300"} size={36} />
                                </div>
                                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                                    No requests sent yet
                                </h3>
                                <p className={`text-sm mb-6 max-w-sm mx-auto ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    Browse coaches and send a request to get started.
                                </p>
                                <button
                                    onClick={() => setTab("browse")}
                                    className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                                >
                                    <Search size={14} /> Browse Coaches
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {requests.map((r) => {
                                    const statusConfig = {
                                        accepted: { icon: CheckCircle2, bg: isDarkMode ? "bg-primary-900/20" : "bg-primary-50", border: isDarkMode ? "border-primary-800/40" : "border-primary-200", badge: "bg-primary-100 text-primary-700", label: "Accepted" },
                                        denied:   { icon: XCircle,      bg: isDarkMode ? "bg-red-900/20"     : "bg-red-50",     border: isDarkMode ? "border-red-800/40"     : "border-red-200",     badge: "bg-red-100 text-red-700",     label: "Denied" },
                                        pending:  { icon: Clock,        bg: isDarkMode ? "bg-amber-900/10"   : "bg-amber-50/50", border: isDarkMode ? "border-amber-800/30"  : "border-amber-200",   badge: "bg-amber-100 text-amber-700", label: "Pending" },
                                    };
                                    const cfg = statusConfig[r.Status] || statusConfig.pending;
                                    const StatusIcon = cfg.icon;
                                    return (
                                        <div
                                            key={r.RequestID}
                                            className={`flex items-center justify-between rounded-xl border p-4 transition-colors ${cfg.bg} ${cfg.border}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getAvatarColor(r.CoachAdminID)} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}>
                                                    {r.FirstName?.[0]}{r.LastName?.[0]}
                                                </div>
                                                <div>
                                                    <p className={`font-semibold text-[15px] ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                                        {r.FirstName} {r.LastName}
                                                    </p>
                                                    <p className={`text-xs mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                                        {r.Email} &middot; {r.RequestDate}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg ${cfg.badge}`}>
                                                <StatusIcon size={13} />
                                                {cfg.label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
